import { router } from '@inertiajs/vue3'

import type { MenuItem, MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import type { ActionCategory, ActionCommand } from '@/domains/command-center/types'

import {
  normalizeItemName,
  SINGLE_MENU_ITEM_ICON_MAPPER
} from '@/domains/admin-panel/components/molecules/menu/twn-single-menu-item/TwnSingleMenuItem.utils'

const FALLBACK_ICON = 'arrow_circle_right'

/**
 * Read by `commandGenerator` to decide which group gets ranked and capped. It
 * lives here because this file is the only thing that produces the group.
 */
const NAVIGATION_CATEGORY_ID = 'cat-navigation'

/** A leaf item and a section that is its own destination both carry these three. */
type Destination = Pick<MenuItem, 'name' | 'path' | 'keywords'>

/**
 * Every admin page the sidebar can reach, as command palette rows.
 *
 * Takes the sections `useMenuStore` exposes. The server has already withheld the
 * ones the user may not see, so this function never asks, and a withheld section
 * cannot appear in the output.
 *
 * Pure on purpose: the store lives in `useCommandCenter`, and this stays a plain
 * function like its three sibling handlers.
 */
const getNavigationCategory = (sections: MenuSection[]): ActionCategory => ({
  id: NAVIGATION_CATEGORY_ID,
  label: 'Pages',
  actions: buildActions(sections)
})

const buildActions = (sections: MenuSection[]): ActionCommand[] => {
  const seenPaths = new Set<string>()

  return sections.flatMap(section => destinationsIn(section)).filter(action => isFirstSighting(action, seenPaths))
}

/**
 * A section is itself a destination when it carries a path and no children —
 * Home, Orders and Integrations are all built that way.
 */
const destinationsIn = (section: MenuSection): ActionCommand[] => {
  if (section.items?.length) {
    return section.items.filter(isInternal).map(item => toAction(item, section))
  }

  return isInternalSection(section) ? [toAction(section, section)] : []
}

const toAction = (destination: Destination, section: MenuSection): ActionCommand => ({
  id: `nav-${destination.path}`,
  type: 'action',
  label: destination.name,
  // The section name, so "Printers" reads under "POS & Printing" rather than alone.
  subLabel: section.name,
  icon: iconFor(destination.name, section),
  // A section's own keywords reach every page under it.
  keywords: [...(destination.keywords ?? []), ...(section.keywords ?? [])],
  execute: () => router.get(destination.path),
  onSuccess: 'closeMenu'
})

/**
 * The sidebar looks its leaf icons up by label rather than taking them from the
 * payload, so a row here reads the same map and stays in step with it.
 */
const iconFor = (name: string, section: MenuSection): string =>
  SINGLE_MENU_ITEM_ICON_MAPPER[normalizeItemName(name)] ?? section.icon ?? FALLBACK_ICON

/**
 * A palette row is an admin-panel page, and nothing else. Nova destinations reach
 * the payload three ways, and none of them is stopped upstream:
 *
 * - `nova-settings`, a sibling of Configuration and Branding inside the `Settings`
 *   section (`SettingsMenuBuilder.php:21`). `useMenuStore` does drop Nova, but
 *   only whole sections — a section survives when any one item is an admin page,
 *   and keeps its Nova siblings.
 * - `/dashboards/main`, the hardcoded `Legacy` section, appended to
 *   `settingsMenuSections` after that filter has already run.
 * - `/resources/customer-segments`, which `NovaMainMenuBuilder.php:205` serves
 *   instead of the admin path on a non-admin request.
 *
 * So the guard is here, per destination, rather than trusting the section.
 */
const ADMIN_PATH_PREFIX = '/admin/'

const isAdminPath = (path: string | undefined): boolean => Boolean(path?.startsWith(ADMIN_PATH_PREFIX))

const isInternal = (item: MenuItem): boolean => isAdminPath(item.path) && !item.external

const isInternalSection = (section: MenuSection): boolean => isAdminPath(section.path) && !section.externalUrl

const isFirstSighting = (action: ActionCommand, seen: Set<string>): boolean => {
  if (seen.has(action.id)) {
    return false
  }

  seen.add(action.id)

  return true
}

export { getNavigationCategory, NAVIGATION_CATEGORY_ID }
